<template>
  <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
    <div v-if="loading" class="text-center">
      <div class="animate-spin rounded-full h-12 w-12 border-4 border-zebu-orange border-b-transparent mx-auto"></div>
      <p class="mt-4 text-lg text-gray-600">Vérification de votre paiement...</p>
    </div>

    <div v-else-if="error" class="text-center">
      <div class="rounded-full bg-red-100 h-20 w-20 flex items-center justify-center mx-auto mb-6">
        <svg class="h-10 w-10 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </div>
      <h1 class="text-3xl font-bold text-gray-900 mb-4">Une erreur est survenue</h1>
      <p class="text-lg text-gray-600 mb-8">{{ error }}</p>
      <router-link
        to="/public-subscribe"
        class="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-zebu-orange hover:bg-zebu-orange/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-zebu-orange"
      >
        Réessayer
      </router-link>
    </div>

    <div v-else class="text-center">
      <div class="rounded-full bg-green-100 h-20 w-20 flex items-center justify-center mx-auto mb-6">
        <svg class="h-10 w-10 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
        </svg>
      </div>

      <h1 class="text-3xl font-bold text-gray-900 mb-4">
        Bienvenue chez Zebunet !
      </h1>
      <p class="text-lg text-gray-600 mb-8">
        Votre adhésion a été validée avec succès. Un email de confirmation vous a été envoyé.
      </p>

      <div class="bg-white rounded-xl shadow-md p-6 mb-8">
        <h2 class="text-xl font-semibold text-gray-900 mb-4">Prochaines étapes</h2>
        <ul class="space-y-4 text-left">
          <li class="flex items-start">
            <svg class="h-6 w-6 text-green-500 mr-3 mt-0.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Confirmez votre compte pour accéder à votre espace membre</span>
          </li>
          <li class="flex items-start">
            <svg class="h-6 w-6 text-green-500 mr-3 mt-0.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Découvrez les projets disponibles</span>
          </li>
          <li class="flex items-start">
            <svg class="h-6 w-6 text-green-500 mr-3 mt-0.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Commencez à financer vos premiers projets</span>
          </li>
        </ul>
      </div>

      <div class="bg-white rounded-xl p-4 space-x-4">
        <router-link
          to="/login"
          class="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-zebu-orange hover:bg-zebu-orange/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-zebu-orange"
        >
          Se connecter
        </router-link>
        <router-link
          to="/dashboard"
          class="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-lg shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-zebu-orange"
        >
          Retour à l'accueil
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { memberareaService } from '@/services/memberarea.service'
import { useToast } from 'vue-toastification'

const route = useRoute()
const router = useRouter()
const toast = useToast()

const loading = ref(true)
const error = ref(null)

onMounted(async () => {
  const sessionId = route.query.session_id
  
  if (!sessionId) {
    error.value = "Session de paiement invalide"
    loading.value = false
    return
  }

  try {
    const response = await memberareaService.verifyPublicMembershipPayment(sessionId)
    if (!response.success) {
      throw new Error(response.message || "Échec de la vérification du paiement")
    }
    loading.value = false
  } catch (err) {
    error.value = err.message || "Une erreur est survenue lors de la vérification du paiement"
    loading.value = false
  }
})
</script> 